import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import { useEffect, useRef, useState } from 'react';
import dynamicfunction from '../../../utils/dynamicfunction';
import {
  getCorpKycGeneralInfo,
  getCorpKycGeneralInfoDropdowns,
  getGeneralInformationFormData,
  postGeneralInformation,
  saveCorpKycGeneralInfo,
} from '../../../store/actions/corporateAction';
import { cn, formatDate, getContentKeyAccToLang, setToken, showToast } from '../../../utils/utils';
import {
  general_information_managerList_validation_schema,
  general_information_sharePersonList_validation_schema,
  general_information_validation_schema,
} from '../../../validations_schemas/general_information_schema';
import { Chips, ErrorText, Text } from '../components';
import { RowView, Dropdown, DatePicker } from '../../../components';
import CustomInput from '../../../components/CustomInput';
import Button from '../../../components/Button';
import { fontSizes } from '../../../constants/constants';
import dayjs from 'dayjs';
import { useNavigate, useSearchParams } from 'react-router';
import icons from '../../../constants/icons';
import { ReactSVG } from 'react-svg';
import { Modal } from '@mui/material';
import HijriDatePicker from '../../../components/HijriDatePicker';
import CustomModal from '../../../components/CustomModal';
import { logoutUser } from '../../../store/actions/authActions';
import { setClientApplicantId, setClientApplicationId, setClientId, setKsaCorporate, setRejectionMsg } from '../../../store/slices/constantSlice';
import { languages } from '../../../config/config';
import { changeLanguage } from '../../../store/actions/settingAction';
import KeepValuesOnLanguageChange from '../../../components/KeepValuesOnLanguageChange';

const GeneralInformation = ({ onDone }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { isArabic, TextWeight } = dynamicfunction();

  const { corporate, language } = useSelector((state) => state.content);
  const { general_information_dropdowns, general_information_data, corporate_post_loading } = useSelector((state) => state.corporate);
  const { client_application_id, is_kyc, rejection_msg, is_ksa_corporate } = useSelector((state) => state.constant);

  const [searchParams, setSearchParams] = useSearchParams();

  const [sharePersonIsModalOpen, setSharePersonIsModalOpen] = useState(false);
  const [applicationRejectedModal, setApplicationRejectedModal] = useState(false);
  const applicationRegectedToogleModal = () => {
    setApplicationRejectedModal(!applicationRejectedModal);
  };

  const handleRejectionModalClose = () => {
    setApplicationRejectedModal(false);
    dispatch(setRejectionMsg(null));
  };

  const [sharePersonList, setSharePersonList] = useState([]);
  const [selectedSharePerson, setSelectedSharePerson] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [managerList, setManagerList] = useState([]);
  const [selectedManager, setSelectedManager] = useState(null);

  // Unsaved form values captured on language switch; in memory only, so a hard reload clears them.
  const draftValues = useRef(null);
  const listsEdited = useRef(false);

  const findCountry = (id) => general_information_dropdowns?.countries?.find((item) => item?.id === id) || null;


  // Add new share person
  const handleAddSharePerson = (values, resetForm) => {
    if (!values.percentageOwnership && !values.birthPlace && !values.idNumber && !values.name) return; // prevent adding empty
    listsEdited.current = true;
    setSharePersonList([
      ...sharePersonList,
      {
        name: values.name,
        percentageOwnership: values.percentageOwnership,
        birthPlace: values.birthPlace,
        idNumber: values.idNumber,
      },
    ]);
    resetForm();
  };
  // Delete share person
  const handleDeleteSharePerson = (index) => {
    const updated = sharePersonList.filter((_, i) => i !== index);
    listsEdited.current = true;
    setSharePersonList(updated);
  };

  // Add new manager
  const handleAddManager = (values, resetForm) => {
    if (!values.name && !values.position) return; // prevent adding empty
    listsEdited.current = true;
    setManagerList([
      ...managerList,
      {
        name: values.name,
        position: values.position,
      },
    ]);
    resetForm();
  };

  // Delete manager
  const handleDeleteManager = (index) => {
    const updated = managerList.filter((_, i) => i !== index);
    listsEdited.current = true;
    setManagerList(updated);
  };

  useEffect(() => {
    if (typeof rejection_msg === 'string') {
      const comment = rejection_msg.trim();
      if (comment) {
        setApplicationRejectedModal(true);
      }
    }
  }, []);

  useEffect(() => {
    getData();
  }, [language]);

  const getData = async () => {
    const accessToken = searchParams.get('accessToken');
    const applicantTypeId = searchParams.get('ApplicantTypeId');
    const clientIdParam = searchParams.get('ClientId');
    const clientApplicationId = searchParams.get('ClientApplicationId');
    const languageId = searchParams.get('languageId');

    if (accessToken) {
      setToken(accessToken);
      dispatch(setClientApplicantId(Number(applicantTypeId)));
      dispatch(setClientApplicationId(Number(clientApplicationId)));
      dispatch(setClientId(Number(clientIdParam)));
      dispatch(setKsaCorporate(Number(applicantTypeId) === 1067));
      const resolvedLanguage = languageId === '3' ? languages?.eng : languages?.arb;
      setSearchParams({}, { replace: true });

      if (resolvedLanguage !== language) {
        const languageId = language !== 'English' ? 3 : 4
        dispatch(changeLanguage(languageId));
        // dispatch(setLanguage({ language: resolvedLanguage }));
        return;
      }
    }

    await dispatch(getCorpKycGeneralInfoDropdowns());

    // After a language switch, keep the manager/share person lists the user already edited.
    const setLists = (res) => {
      if (listsEdited.current) return;
      setManagerList(res?.result?.managerList);
      setSharePersonList(res?.result?.sharePersonList);
    };

    is_kyc
      ? await dispatch(getCorpKycGeneralInfo(clientApplicationId ?? client_application_id, setLists))
      : await dispatch(getGeneralInformationFormData(clientApplicationId ?? client_application_id, setLists));
  };

  const getContent = (key) => {
    return getContentKeyAccToLang(corporate?.corporate_general_information?.data?.attributes, key, isArabic);
  };

  const onSubmit = (values) => {
    // if (sharePersonList?.length < 1) {
    //   showToast({
    //     icon: 'error',
    //     title: getMainContent('please_add_atleast_one_owner'),
    //   });
    //   return;
    // } else if (managerList?.length < 1) {
    //   showToast({
    //     icon: 'error',
    //     title: getMainContent('please_add_one_director'),
    //   });
    //   return;
    // }

    const isSharePersonInvalid = sharePersonList?.some(
      (item) =>
        !item?.percentageOwnership ||
        !item?.birthPlace ||
        !item?.idNumber ||
        !item?.name,
    );

    // Check managerList fields
    const isManagerInvalid = managerList?.some(
      (item) =>
        !item?.name ||
        !item?.position,
    );

    if (isSharePersonInvalid) {
      showToast({
        icon: 'error',
        title: getContent('please_fill_all_shareholder_fields'),
      });
      return;
    } else if (isManagerInvalid) {
      showToast({
        icon: 'error',
        title: getContent('please_fill_all_manager_fields'),
      });
      return;
    }

    try {
      const api_data = {
        commercialRegistrationNameEn: values?.commercialRegistrationNameEn,
        commercialRegistrationName: values?.commercialRegistrationName,
        commercialRegistrationNo: values?.commercialRegistrationNo,
        expiryDateOfCR: values?.expiryDateOfCR,
        expiryDateOfCRGRG: values?.expiryDateOfCRGRG,
        unifiedNumber: Number(values?.unifiedNumber),
        countryOfRegistration: Number(values?.countryOfRegistration?.id),
        dateOfIncorporation: values?.dateOfIncorporation,
        dateOfIncorporationGRG: values?.dateOfIncorporationGRG,
        countryOfPracticingBusiness: Number(values?.countryOfPracticingBusiness?.id),
        paidUpCapital: Number(values?.paidUpCapital),
        annualTurnover: values?.annualTurnover,
        noOfEmployees: Number(values?.noOfEmployees),
        mainActivity: values?.mainActivity,
        legalStructure: values?.legalStructure,
        legalStructureOther: values?.legalStructureOther,
        website: values?.website,
        managerList: managerList,
        sharePersonList: sharePersonList,
        corpClientApplicationId: Number(client_application_id),
      };

      is_kyc
        ? dispatch(
          saveCorpKycGeneralInfo(api_data, () => {
            values?.isSaveExist ? navigate('/funds/available-funds') : onDone();
          }),
        )
        : dispatch(
          postGeneralInformation(api_data, () => {
            values?.isSaveExist ? dispatch(logoutUser()) : onDone();
          }),
        );
    } catch (error) { }
  };

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={draftValues.current ? {
          ...draftValues.current,
          // Re-pick dropdown objects from the refetched list so labels show in the new language.
          countryOfRegistration: findCountry(draftValues.current?.countryOfRegistration?.id),
          countryOfPracticingBusiness: findCountry(draftValues.current?.countryOfPracticingBusiness?.id),
        } : {
          commercialRegistrationNameEn: general_information_data?.commercialRegistrationNameEn || '',
          commercialRegistrationName: general_information_data?.commercialRegistrationName || '',
          commercialRegistrationNo: general_information_data?.commercialRegistrationNo || '',
          expiryDateOfCR: general_information_data?.expiryDateOfCR ? formatDate(general_information_data?.expiryDateOfCR) : null,
          expiryDateOfCRGRG: general_information_data?.expiryDateOfCRGRG
            ? dayjs(general_information_data?.expiryDateOfCRGRG).format('YYYY-MM-DD')
            : null,
          unifiedNumber: general_information_data?.unifiedNumber || '',
          countryOfRegistration:
            general_information_dropdowns?.countries?.find((item) => item?.id === general_information_data?.countryOfRegistration) || null,
          dateOfIncorporation: general_information_data?.dateOfIncorporation
            ? formatDate(general_information_data?.dateOfIncorporation)
            : null,
          dateOfIncorporationGRG: general_information_data?.dateOfIncorporationGRG
            ? dayjs(general_information_data?.dateOfIncorporationGRG).format('YYYY-MM-DD')
            : null,
          countryOfPracticingBusiness:
            general_information_dropdowns?.countries?.find((item) => item?.id === general_information_data?.countryOfPracticingBusiness) || null,
          paidUpCapital: general_information_data?.paidUpCapital || '',
          annualTurnover: general_information_data?.annualTurnover || '',
          mainActivity: general_information_data?.mainActivity || '',
          noOfEmployees: general_information_data?.noOfEmployees || '',
          website: general_information_data?.website || '',
          legalStructure: general_information_data?.legalStructure || '',
          legalStructureOther: general_information_data?.legalStructureOther || '',

          isSaveExist: false,
          is_ksa: is_ksa_corporate,
        }}
        validationSchema={general_information_validation_schema}
        onSubmit={(values) => {
          onSubmit(values);
        }}
        validateOnChange={true}
        validateOnBlur={true}
      >
        {({ handleSubmit, setFieldValue, values, touched, errors, setFieldTouched }) => {
          return (
            <div className=''>
              <KeepValuesOnLanguageChange onLanguageChange={(currentValues) => (draftValues.current = currentValues)} />
              <RowView className='gap-7' isResponsive>
                <CustomInput
                  label={getContent('name_en')}
                  value={values?.commercialRegistrationNameEn}
                  maxLength={100}
                  isRounded
                  isRequired={true}
                  onChange={(e) => {
                    setFieldValue('commercialRegistrationNameEn', e.target.value);
                    setFieldTouched('commercialRegistrationNameEn', true, false);
                  }}
                  placeholder={getContent('placeholder_name_en')}
                  errorMessage={getContent(touched?.commercialRegistrationNameEn ? errors?.commercialRegistrationNameEn : '')}
                />
                <CustomInput
                  label={getContent('name_ar')}
                  value={values?.commercialRegistrationName}
                  maxLength={100}
                  onChange={(e) => {
                    setFieldValue('commercialRegistrationName', e.target.value);
                    setFieldTouched('commercialRegistrationName', true, false);
                  }}
                  isRequired={is_ksa_corporate}
                  placeholder={getContent('placeholder_name_ar')}
                  errorMessage={getContent(touched?.commercialRegistrationName ? errors?.commercialRegistrationName : '')}
                />
                <CustomInput
                  label={getContent('cr_no')}
                  value={values?.commercialRegistrationNo}
                  isRounded
                  isRequired={true}
                  maxLength={15}
                  onChange={(e) => {
                    setFieldValue('commercialRegistrationNo', e.target.value);
                    setFieldTouched('commercialRegistrationNo', true, false);
                  }}
                  labelDivClass={"h-9 sm:h-6"}
                  placeholder={getContent('placeholder_enter_no')}
                  errorMessage={getContent(touched?.commercialRegistrationNo ? errors?.commercialRegistrationNo : '')}
                />
              </RowView>

              <RowView className='gap-7  pt-3 sm:pt-5' isResponsive>
                <HijriDatePicker
                  label={getContent('cr_ed_hijri')}
                  selectedDate={values?.expiryDateOfCR ? values?.expiryDateOfCR : null}
                  onChange={(val) => {
                    setFieldValue('expiryDateOfCR', val ? val : null);
                    setFieldTouched('expiryDateOfCR', false);
                  }}
                  labelDivClass={"h-9 sm:h-6 md:h-9 lg:h-6"}
                  minDate={new Date()}
                  isRequired
                  errorMessage={getContent(touched?.expiryDateOfCR ? errors?.expiryDateOfCR : '')}
                />
                <DatePicker
                  label={getContent('cr_ed_georgian')}
                  selectedDate={values?.expiryDateOfCRGRG ? new Date(values?.expiryDateOfCRGRG) : null}
                  onChange={(val) => {
                    setFieldValue('expiryDateOfCRGRG', val ? val.toISOString().split('T')[0] : null);
                    setFieldTouched('expiryDateOfCRGRG', false);
                  }}
                  labelDivClass={"h-9 sm:h-6 md:h-9 lg:h-6"}
                  minDate={new Date()}
                  isRequired
                  errorMessage={getContent(touched?.expiryDateOfCRGRG ? errors?.expiryDateOfCRGRG : '')}
                />
                <CustomInput
                  label={getContent('unified')}
                  value={values?.unifiedNumber}
                  isRounded
                  isRequired={true}
                  maxLength={10}
                  onChange={(e) => {
                    setFieldValue('unifiedNumber', e.target.value);
                    setFieldTouched('unifiedNumber', true, false);
                  }}
                  labelDivClass={"h-6 md:h-9 lg:h-6"}
                  placeholder={getContent('placeholder_enter_no')}
                  errorMessage={getContent(touched?.unifiedNumber ? errors?.unifiedNumber : '')}
                />
              </RowView>

              <RowView className='gap-7  pt-3 sm:pt-5' isResponsive>
                <Dropdown
                  label={getContent('country_registration')}
                  placeholder={getContent('placeholder_country_registration')}
                  options={general_information_dropdowns?.countries}
                  selectedValue={values?.countryOfRegistration}
                  valueKey={'name'}
                  isRequired
                  isSearch
                  onSelect={(val) => {
                    setFieldValue('countryOfRegistration', val);
                    setFieldTouched('countryOfRegistration', false);
                  }}
                  labelDivClass={"h-6 md:h-9 lg:h-6"}
                  errorMessage={getContent(touched?.countryOfRegistration ? errors?.countryOfRegistration : '')}
                />
                <HijriDatePicker
                  label={getContent('doi_hijri')}
                  selectedDate={values?.dateOfIncorporation ? values?.dateOfIncorporation : null}
                  onChange={(val) => {
                    setFieldValue('dateOfIncorporation', val ? val : null);
                    setFieldTouched('dateOfIncorporation', false);
                  }}
                  maxDate={new Date()}
                  isRequired
                  labelDivClass={"h-6 md:h-9 lg:h-6"}
                  errorMessage={getContent(touched?.dateOfIncorporation ? errors?.dateOfIncorporation : '')}
                />
                <DatePicker
                  label={getContent('doi_georgian')}
                  selectedDate={values?.dateOfIncorporationGRG ? new Date(values?.dateOfIncorporationGRG) : null}
                  onChange={(val) => {
                    setFieldValue('dateOfIncorporationGRG', val ? val.toISOString().split('T')[0] : null);
                    setFieldTouched('dateOfIncorporationGRG', false);
                  }}
                  maxDate={new Date()}
                  isRequired
                  labelDivClass={"h-9 sm:h-6 md:h-9 lg:h-6"}
                  errorMessage={getContent(touched?.dateOfIncorporationGRG ? errors?.dateOfIncorporationGRG : '')}
                />
              </RowView>

              <RowView className='gap-7 pt-3 sm:pt-5' isResponsive>
                <Dropdown
                  label={getContent('country_of_practicing_business')}
                  placeholder={getContent('placeholder_country_of_practicing_business')}
                  options={general_information_dropdowns?.countries}
                  selectedValue={values?.countryOfPracticingBusiness}
                  valueKey={'name'}
                  isRequired
                  isSearch
                  onSelect={(val) => {
                    setFieldValue('countryOfPracticingBusiness', val);
                    setFieldTouched('countryOfPracticingBusiness', false);
                  }}
                  labelDivClass={"h-9 sm:h-6"}
                  errorMessage={getContent(touched?.countryOfPracticingBusiness ? errors?.countryOfPracticingBusiness : '')}
                />
                <CustomInput
                  label={getContent('paid_up_capital')}
                  value={values?.paidUpCapital}
                  isRounded
                  isRequired={true}
                  onChange={(e) => {
                    setFieldValue('paidUpCapital', e.target.value);
                    setFieldTouched('paidUpCapital', true, false);
                  }}
                  placeholder={getContent('placeholder_paid_up_capital')}
                  errorMessage={getContent(touched?.paidUpCapital ? errors?.paidUpCapital : '')}
                  maxLength={12}
                />
                <Dropdown
                  label={getContent('annual_turnover')}
                  placeholder={getContent('placeholder_annual_turnover')}
                  options={general_information_dropdowns?.annualTurnover}
                  selectedValue={general_information_dropdowns?.annualTurnover?.find((opt) => opt?.id === values?.annualTurnover)}
                  valueKey={'name'}
                  isRequired
                  onSelect={(val) => {
                    setFieldValue('annualTurnover', val?.id);
                    setFieldTouched('annualTurnover', false);
                  }}
                  errorMessage={getContent(touched?.annualTurnover ? errors?.annualTurnover : '')}
                />
              </RowView>

              <RowView className='gap-7 pt-3 sm:pt-5' isResponsive>
                <CustomInput
                  label={getContent('number_of_employees')}
                  value={values?.noOfEmployees}
                  isRounded
                  isRequired={true}
                  maxLength={5}
                  onChange={(e) => {
                    setFieldValue('noOfEmployees', e.target.value);
                    setFieldTouched('noOfEmployees', true, false);
                  }}
                  placeholder={getContent('placeholder_number_of_employees')}
                  errorMessage={getContent(touched?.noOfEmployees ? errors?.noOfEmployees : '')}
                />
                <CustomInput
                  label={getContent('main_business_en')}
                  value={values?.mainActivity}
                  isRounded
                  isRequired={true}
                  onChange={(e) => {
                    setFieldValue('mainActivity', e.target.value);
                    setFieldTouched('mainActivity', true, false);
                  }}
                  maxLength={500}
                  placeholder={getContent('placeholder_main_business_en')}
                  errorMessage={getContent(touched?.mainActivity ? errors?.mainActivity : '')}
                />
                <CustomInput
                  label={getContent('website')}
                  value={values?.website}
                  isRounded
                  onChange={(e) => {
                    setFieldValue('website', e.target.value);
                    setFieldTouched('website', true, false);
                  }}
                  isRequired={false}
                  placeholder={getContent('placeholder_website')}
                  errorMessage={getContent(touched?.website ? errors?.website : '')}
                />
              </RowView>

              <div className='w-full pt-3 sm:pt-5'>
                <Chips
                  label={getContent('legal_structure')}
                  value={values?.legalStructure}
                  onChange={(e) => {
                    setFieldValue('legalStructure', e);
                    setFieldTouched('legalStructure', false);
                  }}
                  options={general_information_dropdowns?.legalStructures}
                />

                <ErrorText text={getContent(touched?.legalStructure ? errors?.legalStructure : '')} className='block' />
              </div>
              {values?.legalStructure === 1088 && (
                <div className='pt-3 sm:pt-5'>
                  <CustomInput
                    isRounded
                    value={values.legalStructureOther}
                    onChange={(e) => {
                      setFieldValue(`legalStructureOther`, e.target.value);
                      setFieldTouched('legalStructureOther', true, false);
                    }}
                    placeholder={getContent('legal_structure')}
                    errorMessage={getContent(touched?.legalStructureOther ? errors?.legalStructureOther : '')}
                  />
                </div>
              )}

              {/* sharePerson Form */}
              <Formik
                initialValues={{
                  name: '',
                  percentageOwnership: '',
                  birthPlace: '',
                  idNumber: '',
                }}
                validationSchema={general_information_sharePersonList_validation_schema}
                onSubmit={(values, { resetForm }) => handleAddSharePerson(values, resetForm)}
                validateOnChange={true}
                validateOnBlur={true}
              >
                {({ handleSubmit, setFieldValue, values, resetForm, setFieldError, errors, setFieldTouched, touched }) => {
                  return (
                    <div className='pt-5'>
                      <RowView className='flex flex-wrap items-center justify-between'>
                        <Text
                          text={getContent('owners_percentage_plus_heading')}
                          size={fontSizes.h18}
                          isTruncate={false}
                          weight={TextWeight.semiBold}
                          className={'block'}
                        />
                        <RowView className='gap-3 w-full justify-start sm:justify-end flex-wrap mt-3 xl:mt-0 mb-4'>
                          <Button
                            text={getContent('add_more_btn')}
                            color='text-white hover:text-primary'
                            bgColor='bg-primary hover:bg-transparent hover:border-primary'
                            className='border border-transparent w-full sm:w-44 px-0 text-center'
                            onClick={handleSubmit}
                          />
                          <Button
                            text={getContent('clear')}
                            color='text-primary hover:text-white'
                            bgColor='bg-white hover:bg-primary'
                            className='border border-primary w-full sm:w-44 px-0 text-center'
                            onClick={resetForm}
                          />
                        </RowView>
                      </RowView>

                      <RowView className='gap-7 pt-3 sm:pt-5' isResponsive>
                        <CustomInput
                          isRounded
                          isRequired={true}
                          label={getContent('percentage_of_ownership')}
                          value={values.percentageOwnership}
                          maxLength={3}
                          onChange={(e) => {
                            setFieldValue('percentageOwnership', e.target.value);
                            setFieldTouched('percentageOwnership', true, false);
                          }}
                          placeholder={getContent('placeholder_percentage_of_ownership')}
                          errorMessage={getContent(touched?.percentageOwnership ? errors?.percentageOwnership : '')}
                        />

                        <CustomInput
                          isRounded
                          isRequired={true}
                          label={getContent('place_of_birth')}
                          value={values.birthPlace}
                          onChange={(e) => {
                            setFieldValue('birthPlace', e.target.value);
                            setFieldTouched('birthPlace', true, false);
                          }}
                          placeholder={getContent('placeholder_place_of_birth')}
                          errorMessage={getContent(touched?.birthPlace ? errors?.birthPlace : '')}
                        />

                        <CustomInput
                          isRounded
                          isRequired={true}
                          label={getContent('id_number')}
                          value={values.idNumber}
                          maxLength={10}
                          onChange={(e) => {
                            setFieldValue('idNumber', e.target.value);
                            setFieldTouched('idNumber', true, false);
                          }}
                          placeholder={getContent('placeholder_enter_no')}
                          errorMessage={getContent(touched?.idNumber ? errors?.idNumber : '')}
                        />
                      </RowView>

                      <RowView className='gap-7 pt-3 sm:pt-5'>
                        <CustomInput
                          isRounded
                          isRequired={true}
                          label={getContent('owners_percentage_plus_name')}
                          value={values?.name}
                          onChange={(e) => {
                            setFieldValue('name', e.target.value);
                            setFieldTouched('name', true, false);
                          }}
                          placeholder={getContent('placeholder_percentage_plus_name')}
                          errorMessage={getContent(touched?.name ? errors?.name : '')}
                        />
                      </RowView>
                    </div>
                  );
                }}
              </Formik>
              {/* sharePersonList List */}
              {sharePersonList.length > 0 && (
                <div className='border border-border rounded-lg mt-4 overflow-x-auto'>
                  <RowView className='bg-primary/10 px-4 py-3 w-full gap-2'>
                    <Text
                      text={getContent('percentage_of_ownership')}
                      color='text-primary'
                      className='min-w-[250px]'
                      weight={TextWeight.medium}
                      size={fontSizes.h14}
                    />
                    <Text
                      text={getContent('place_of_birth')}
                      color='text-primary'
                      className='min-w-[150px]'
                      weight={TextWeight.medium}
                      size={fontSizes.h14}
                    />
                    <Text
                      text={getContent('id_number')}
                      color='text-primary'
                      className='min-w-[150px]'
                      weight={TextWeight.medium}
                      size={fontSizes.h14}
                    />
                    <Text
                      text={getContent('owners_percentage_plus_name')}
                      color='text-primary'
                      className='min-w-[150px]'
                      weight={TextWeight.medium}
                      size={fontSizes.h14}
                    />
                    <Text
                      text={getContent('action')}
                      color='text-primary'
                      align={cn(isArabic ? 'mr-auto text-left' : 'ml-auto text-right')}
                      className={'min-w-[60px]'}
                      weight={TextWeight.medium}
                      size={fontSizes.h14}
                    />
                  </RowView>

                  {sharePersonList.map((value, index) => (
                    <RowView key={index} className='px-4 py-3 w-full border-b border-b-border gap-2'>
                      <Text
                        text={value.percentageOwnership}
                        color='text-primary'
                        className='min-w-[250px] max-w-[250px] line-clamp-2'
                        weight={TextWeight.medium}
                        size={fontSizes.h14}
                      />
                      <Text
                        text={value.birthPlace}
                        color='text-primary'
                        className='min-w-[150px] max-w-[150px] line-clamp-2'
                        weight={TextWeight.medium}
                        size={fontSizes.h14}
                      />
                      <Text
                        text={value.idNumber}
                        color='text-primary'
                        className='min-w-[150px] max-w-[150px] line-clamp-2'
                        weight={TextWeight.medium}
                        size={fontSizes.h14}
                      />
                      <Text
                        text={value.name}
                        color='text-primary'
                        className='min-w-[150px] max-w-[150px] line-clamp-2'
                        weight={TextWeight.medium}
                        size={fontSizes.h14}
                      />
                      <RowView className={cn('min-w-[60px] gap-4', isArabic ? 'mr-auto justify-start' : 'ml-auto justify-end')}>
                        <ReactSVG src={icons.delete_icon} className='cursor-pointer' onClick={() => handleDeleteSharePerson(index)} />
                        <ReactSVG
                          src={icons.edit_icon}
                          className='cursor-pointer'
                          onClick={() => {
                            setSelectedSharePerson({ ...value, index });
                            setSharePersonIsModalOpen(true);
                          }}
                        />
                      </RowView>
                    </RowView>
                  ))}
                </div>
              )}

              {/* Managers Form */}
              <Formik
                initialValues={{ name: '', position: '' }}
                validationSchema={general_information_managerList_validation_schema}
                onSubmit={(values, { resetForm }) => handleAddManager(values, resetForm)}
                validateOnChange={true}
                validateOnBlur={true}
              >
                {({ handleSubmit, setFieldValue, values, resetForm, setFieldError, errors, setFieldTouched, touched }) => {
                  return (
                    <div className='pt-5'>
                      <RowView className='flex flex-wrap items-center justify-between'>
                        <Text
                          text={getContent('directors_and_executives_heading')}
                          size={fontSizes.h18}
                          weight={TextWeight.semiBold}
                          isTruncate={false}
                        />
                        <RowView className='gap-3 w-full justify-start sm:justify-end flex-wrap mt-3 xl:mt-0 mb-4'>
                          <Button
                            text={getContent('add_more_btn')}
                            color='text-white hover:text-primary'
                            bgColor='bg-primary hover:bg-transparent hover:border-primary'
                            className='border border-transparent w-full sm:w-44 px-0 text-center'
                            onClick={handleSubmit}
                          />
                          <Button
                            text={getContent('clear')}
                            color='text-primary hover:text-white'
                            bgColor='bg-white hover:bg-primary'
                            className='border border-primary w-full sm:w-44 px-0 text-center'
                            onClick={resetForm}
                          />
                        </RowView>
                      </RowView>

                      <RowView className='gap-7' isResponsive>
                        <CustomInput
                          isRounded
                          isRequired={true}
                          label={getContent('owners_percentage_plus_name')}
                          value={values?.name}
                          onChange={(e) => {
                            setFieldValue('name', e.target.value);
                            setFieldTouched('name', true, false);
                          }}
                          placeholder={getContent('placeholder_percentage_plus_name')}
                          errorMessage={getContent(touched?.name ? errors?.name : '')}
                        />
                        <CustomInput
                          isRounded
                          isRequired={true}
                          label={getContent('position')}
                          value={values?.position}
                          onChange={(e) => {
                            setFieldValue('position', e.target.value);
                            setFieldTouched('position', true, false);
                          }}
                          placeholder={getContent('placeholder_position')}
                          errorMessage={getContent(touched?.position ? errors?.position : '')}
                        />
                      </RowView>
                    </div>
                  );
                }}
              </Formik>

              {/* Manager List */}
              {managerList.length > 0 && (
                <div className='border border-border rounded-lg mt-4 overflow-x-auto'>
                  <RowView className='bg-primary/10 px-4 py-3 w-full gap-2'>
                    <Text
                      text={getContent('owners_percentage_plus_name')}
                      color='text-primary'
                      className='min-w-[150px]'
                      weight={TextWeight.medium}
                      size={fontSizes.h14}
                    />
                    <Text
                      text={getContent('position')}
                      color='text-primary'
                      className='min-w-[150px]'
                      weight={TextWeight.medium}
                      size={fontSizes.h14}
                    />
                    <Text
                      text={getContent('action')}
                      color='text-primary'
                      align={cn(isArabic ? 'mr-auto text-left' : 'ml-auto text-right')}
                      className={'min-w-[60px]'}
                      weight={TextWeight.medium}
                      size={fontSizes.h14}
                    />
                    {/* <div className='min-w-[60px]' /> */}
                  </RowView>

                  {managerList.map((value, index) => (
                    <RowView key={index} className='px-4 py-3 w-full border-b border-b-border gap-2'>
                      <Text
                        text={value.name}
                        color='text-primary'
                        className='min-w-[150px] max-w-[150px] line-clamp-2'
                        weight={TextWeight.medium}
                        size={fontSizes.h14}
                      />
                      <Text
                        text={value.position}
                        color='text-primary'
                        className='min-w-[150px] max-w-[150px] line-clamp-2'
                        weight={TextWeight.medium}
                        size={fontSizes.h14}
                      />
                      <RowView className={cn('min-w-[60px] gap-4', isArabic ? 'mr-auto justify-start' : 'ml-auto justify-end')}>
                        <ReactSVG src={icons.delete_icon} className='cursor-pointer' onClick={() => handleDeleteManager(index)} />
                        <ReactSVG
                          src={icons.edit_icon}
                          className='cursor-pointer'
                          onClick={() => {
                            setSelectedManager({ ...value, index });
                            setIsModalOpen(true);
                          }}
                        />
                      </RowView>
                    </RowView>
                  ))}
                </div>
              )}

              <div className='w-full h-0.5 bg-border mt-3 sm:mt-5'></div>

              <RowView className='gap-7 pt-3 sm:pt-5'>
                <Button
                  text={getContent('save_and_proceed')}
                  color={'text-white hover:text-primary '}
                  bgColor='bg-primary hover:bg-transparent hover:border-primary'
                  className={'border border-transparent w-44 px-0 text-center'}
                  isDisabled={corporate_post_loading}
                  loading={corporate_post_loading}
                  onClick={() => {
                    setFieldValue('isSaveExist', false);
                    handleSubmit();
                  }}
                />
                <Button
                  text={getContent('save_and_exit')}
                  color={'text-primary hover:text-white'}
                  bgColor='bg-white hover:bg-primary'
                  className={`border border-primary w-44 px-0 text-center`}
                  isDisabled={corporate_post_loading}
                  onClick={() => {
                    setFieldValue('isSaveExist', true);
                    handleSubmit();
                  }}
                />
              </RowView>
            </div>
          );
        }}
      </Formik>
      {/* Edit Share Person Modal */}
      <Modal open={sharePersonIsModalOpen} onClose={() => setSharePersonIsModalOpen(false)}>
        <div className='bg-white rounded-lg p-6 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-11/12 md:w-1/2'>
          <Formik
            enableReinitialize
            initialValues={{
              name: selectedSharePerson?.name || '',
              percentageOwnership: selectedSharePerson?.percentageOwnership || '',
              birthPlace: selectedSharePerson?.birthPlace || '',
              idNumber: selectedSharePerson?.idNumber || '',
            }}
            validationSchema={general_information_sharePersonList_validation_schema}
            onSubmit={(values, { resetForm }) => {
              const updatedSharePersons = sharePersonList.map((sharePerson, index) =>
                index === selectedSharePerson.index ? { ...values } : sharePerson,
              );
              listsEdited.current = true;
              setSharePersonList(updatedSharePersons);
              setSharePersonIsModalOpen(false);
              setSelectedSharePerson(null);
              resetForm();
            }}
            validateOnChange={true}
            validateOnBlur={true}
          >
            {({ handleSubmit, setFieldValue, values, setFieldError, errors, setFieldTouched, touched }) => (
              <div>
                <RowView className='items-center justify-between'>
                  <Text text={getContent('edit')} weight={TextWeight.bold} size={fontSizes.h20} />
                  <ReactSVG src={icons.cross_icon} className='cursor-pointer' onClick={() => setSharePersonIsModalOpen(false)} />
                </RowView>
                <RowView className='gap-7 pt-3 sm:pt-5' isResponsive>
                  <CustomInput
                    isRounded
                    isRequired={true}
                    label={getContent('percentage_of_ownership')}
                    value={values.percentageOwnership}
                    onChange={(e) => {
                      if (/^\d*\.?\d*$/.test(e.target.value)) {
                        setFieldValue('percentageOwnership', e.target.value);
                        setFieldTouched('percentageOwnership', true, false);
                      }
                    }}
                    placeholder={getContent('placeholder_percentage_of_ownership')}
                    errorMessage={getContent(touched?.percentageOwnership ? errors?.percentageOwnership : '')}
                    maxLength={3}
                  />

                  <CustomInput
                    isRounded
                    isRequired={true}
                    label={getContent('place_of_birth')}
                    value={values.birthPlace}
                    onChange={(e) => {
                      setFieldValue('birthPlace', e.target.value);
                      setFieldTouched('birthPlace', true, false);
                    }}
                    placeholder={getContent('placeholder_place_of_birth')}
                    errorMessage={getContent(touched?.birthPlace ? errors?.birthPlace : '')}
                  />

                  <CustomInput
                    isRounded
                    isRequired={true}
                    label={getContent('id_number')}
                    value={values.idNumber}
                    maxLength={10}
                    onChange={(e) => {
                      if (/^[A-Za-z0-9]*$/.test(e.target.value)) {
                        setFieldValue('idNumber', e.target.value);
                        setFieldTouched('idNumber', true, false);
                      }
                    }}
                    placeholder={getContent('placeholder_enter_no')}
                    errorMessage={getContent(touched?.idNumber ? errors?.idNumber : '')}
                  />
                </RowView>

                <RowView className='gap-7 pt-3 sm:pt-5'>
                  <CustomInput
                    isRounded
                    isRequired={true}
                    label={getContent('owners_percentage_plus_name')}
                    value={values?.name}
                    onChange={(e) => {
                      setFieldValue('name', e.target.value);
                      setFieldTouched('name', true, false);
                    }}
                    placeholder={getContent('placeholder_percentage_plus_name')}
                    errorMessage={getContent(touched?.name ? errors?.name : '')}
                  />
                </RowView>
                <RowView className='gap-7 pt-5'>
                  <Button
                    text={getContent('submit_btn')}
                    color='text-white hover:text-primary'
                    bgColor='bg-primary hover:bg-transparent hover:border-primary'
                    className='border border-transparent w-1/3 mx-auto px-0 text-center'
                    onClick={handleSubmit}
                  />
                </RowView>
              </div>
            )}
          </Formik>
        </div>
      </Modal>
      {/* Edit Manager Modal */}
      <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <div className='bg-white rounded-lg p-6 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-11/12 md:w-1/2'>
          <Formik
            enableReinitialize
            initialValues={{
              name: selectedManager?.name || '',
              position: selectedManager?.position || '',
            }}
            validationSchema={general_information_managerList_validation_schema}
            onSubmit={(values, { resetForm }) => {
              const updatedManagers = managerList.map((manager, index) => (index === selectedManager.index ? { ...values } : manager));
              listsEdited.current = true;
              setManagerList(updatedManagers);
              setIsModalOpen(false);
              setSelectedManager(null);
              resetForm();
            }}
            validateOnChange={true}
            validateOnBlur={true}
          >
            {({ handleSubmit, setFieldValue, values, setFieldError, errors, setFieldTouched, touched }) => (
              <div>
                <RowView className='items-center justify-between'>
                  <Text text={getContent('edit')} weight={TextWeight.bold} size={fontSizes.h20} />
                  <ReactSVG src={icons.cross_icon} className='cursor-pointer' onClick={() => setIsModalOpen(false)} />
                </RowView>
                <RowView className='gap-7 pt-5' isResponsive>
                  <CustomInput
                    isRounded
                    isRequired={true}
                    label={getContent('owners_percentage_plus_name')}
                    value={values.name}
                    onChange={(e) => {
                      setFieldValue('name', e.target.value);
                      setFieldTouched('name', true, false);
                    }}
                    placeholder={getContent('placeholder_percentage_plus_name')}
                    errorMessage={getContent(touched?.name ? errors?.name : '')}
                  />
                  <CustomInput
                    isRounded
                    isRequired={true}
                    label={getContent('position')}
                    value={values.position}
                    onChange={(e) => {
                      setFieldValue('position', e.target.value);
                      setFieldTouched('position', true, false);
                    }}
                    placeholder={getContent('placeholder_position')}
                    errorMessage={getContent(touched?.position ? errors?.position : '')}
                  />
                </RowView>
                <RowView className='gap-7 pt-5'>
                  <Button
                    text={getContent('submit_btn')}
                    color='text-white hover:text-primary'
                    bgColor='bg-primary hover:bg-transparent hover:border-primary'
                    className='border border-transparent w-1/3 mx-auto px-0 text-center'
                    onClick={handleSubmit}
                  />
                </RowView>
              </div>
            )}
          </Formik>
        </div>
      </Modal>
      <CustomModal
        isOpen={applicationRejectedModal}
        heading={getContent('application_rejected')}
        color={'text-black'}
        size={fontSizes.h18}
        // loading={}
        isFullWidth
        onClose={applicationRegectedToogleModal}
      >
        <div className='my-5 p-2 border border-border rounded'>
          <Text text={rejection_msg} isTruncate={false} size={fontSizes.h14} color='text-black' weight={TextWeight.medium} className={'block'} />
        </div>
        <RowView className='items-center w-full gap-2 mt-3'>
          <Button
            text={getContent('okay_btn')}
            bgColor='bg-primary'
            className={'w-full px-0 text-center capitalize border border-orange'}
            onClick={() => handleRejectionModalClose()}
          />
        </RowView>
      </CustomModal>
    </>
  );
};

export default GeneralInformation;
